import { Controller } from "@hotwired/stimulus";

// Disables the scheduled-switch fields and shows the retention-discount notice on the downsell form when
// the selected replication group applies its retention discount directly in the POS (e.g. Sonny's, Nexus).
// In that case the switch back to the original plan is managed in the POS, so Rinsed's scheduled-switch
// settings are ignored — disabling them makes that clear and keeps them out of the submitted params.
//
// Attached to the form. The initial state is computed server-side (see
// Admin::DownsellPlansHelper#downsell_scheduled_switch_disable_data) so single-replication-group location
// groups — where the selector renders a hidden input that never fires a change event — are handled on load.
// Subsequent changes are driven by the native change event bubbling up from the replication-group <select>.
export default class extends Controller {
  static targets = ["fieldset", "notice"];
  static values = {
    retentionRgIds: Array,
    initiallyDisabled: Boolean,
  };

  connect() {
    // Prefer the select's current value over the server-computed initial state: the value may have
    // changed before the controller connected (e.g. a selection made before the JS loaded, or a
    // turbo/back-forward form restore). Fall back to the server value when there is no select — the
    // single-replication-group case renders a hidden input instead.
    const select = this.replicationGroupSelect();
    if (select) {
      this.setDisabled(this.appliesRetentionDiscount(select.value));
    } else {
      this.setDisabled(this.initiallyDisabledValue);
    }
  }

  // The replication-group control is either a native <select> or a shadcn select, whose controller
  // dispatches change on its hidden input; both carry the field's name and bubble to the form.
  onReplicationGroupChange(event) {
    const { name, value } = event.target;
    // Only react to the downsell's own replication-group select, not the nested scheduled-switch one
    // (name: ...[plan_scheduled_switch_attributes][replication_group_id]).
    if (!name || !name.endsWith("[replication_group_id]")) return;
    if (name.includes("[plan_scheduled_switch_attributes]")) return;

    this.setDisabled(this.appliesRetentionDiscount(value));
  }

  replicationGroupSelect() {
    return this.element.querySelector(
      'select[name$="[replication_group_id]"]:not([name*="[plan_scheduled_switch_attributes]"]), ' +
        'input[data-select-target="hiddenInput"][name$="[replication_group_id]"]' +
        ':not([name*="[plan_scheduled_switch_attributes]"])',
    );
  }

  appliesRetentionDiscount(replicationGroupId) {
    return this.retentionRgIdsValue
      .map(String)
      .includes(String(replicationGroupId));
  }

  // A disabled <fieldset> disables every descendant form control regardless of the control's own disabled
  // attribute (per the HTML spec), which keeps the controls out of the submitted params. Hidden via inline
  // style, not a class: the fieldset carries Tailwind's `contents`, which would beat a [hidden] rule.
  setDisabled(disabled) {
    this.fieldsetTargets.forEach((fieldset) => {
      fieldset.disabled = disabled;
      fieldset.style.display = disabled ? "none" : "";
    });
    this.noticeTargets.forEach((notice) => {
      notice.hidden = !disabled;
    });
  }
}
